function trackClarityEvent(name) {
  if (typeof window.clarity === 'function') window.clarity('event', name);
}

// Delegated because the drawer's checkout button is re-rendered on every cart update.
// Capture phase on window: a third-party checkout handler stops propagation and
// redirects, so a bubbling listener never sees the click.
window.addEventListener(
  'click',
  (event) => {
    if (event.target.closest('#CartDrawer-Checkout, #checkout')) trackClarityEvent('checkout_clicked');
  },
  true
);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openFresh(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openFresh(cartLink);
      }
    });
  }

  // Refreshes the drawer's contents from the server before opening it.
  // The drawer's own DOM can go stale relative to the real cart state —
  // e.g. when cart_type is "notification", items get added via
  // <cart-notification> and this element's markup is never touched — so
  // clicking the header cart icon must not assume the current DOM is
  // accurate. Falls back to a plain open() if the refresh fails.
  openFresh(triggeredBy) {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => {
        if (!response.ok) throw new Error(`Section fetch failed with status ${response.status}`);
        return response.json();
      })
      .then((sections) => this.renderContents({ sections }, triggeredBy))
      .catch((error) => {
        console.error('cart-drawer: failed to refresh cart contents before opening', error);
        this.syncDiscounts();
        this.open(triggeredBy);
      });
  }

  open(triggeredBy) {
    if (!this.classList.contains('active')) trackClarityEvent('cart_drawer_opened');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, triggeredBy) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // The 'is-empty' class on the host element itself controls header/layout CSS.
    // It's normally cleared by product-form.js via `this.cart`, but that only works
    // when the drawer IS `this.cart` (cart_type "drawer"). When cart_type is
    // "notification", `this.cart` is <cart-notification> instead, so this class is
    // never cleared here otherwise — leaving the drawer stuck looking empty until
    // a full page reload. Clear it directly whenever we render real content.
    if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncDiscounts(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open(triggeredBy);
    });
  }

  syncDiscounts(cart) {
    this.querySelectorAll('cart-discount').forEach((el) => {
      if (typeof el.syncFromCart === 'function') el.syncFromCart(cart);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
